import { yearlyContributions } from './retirement/dcContribution';
import React, { useState, useMemo } from 'react';
import './EmployeeRetirementTable.css';
import config from '../config';

// 분수 모양으로 쓰는 식: 위 / 아래
const Frac = ({ top, bottom }) => (
  <span className="dc-frac"><span className="dc-num">{top}</span><span className="dc-den">{bottom}</span></span>
);
const V = ({ children }) => <i className="dc-var">{children}</i>;

function EmployeeRetirementTable({ employeeData, employee, company, onAddToCart, retirementMeta, plannedLeave, onPlannedLeaveChange }) {
  const [selectedCells, setSelectedCells] = useState(new Set()); // 선택된 셀들의 키 (rowIndex-colIndex 형태)
  const [selectedColumns, setSelectedColumns] = useState(new Set()); // 선택된 컬럼들
  const [monthOverrides, setMonthOverrides] = useState({}); // 월별 산입분 수기 수정 {연월: 0~1}

  // 데이터가 배열인지 확인하고 처리
  const dataArray = Array.isArray(employeeData) ? employeeData : [];



  // 숫자 포맷팅 (Salary 컴포넌트와 동일한 방식)
  const formatCellValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    const stringValue = String(value).trim();
    
    // 완벽한 숫자인지 확인 (정수 또는 소수)
    const numberRegex = /^-?\d+(\.\d+)?$/;
    if (numberRegex.test(stringValue)) {
      const numValue = parseFloat(stringValue);
      if (!isNaN(numValue)) {
        // 소수 첫째자리 올림
        const roundedValue = Math.ceil(numValue);
        // 천 단위 콤마 추가
        return roundedValue.toLocaleString('ko-KR');
      }
    }
    
    return stringValue;
  };

  // 헤더 포맷팅 함수 (Salary 컴포넌트와 동일)
  const formatHeader = (header) => {
    if (!header) return '';
    return header.split(config.FIELD_DELIMITER)
      .map(part => part.trim())
      .filter(part => part.length > 0 && part.toLowerCase() !== 'nan');
  };

  // 한글과 영문자를 구분하여 정확한 텍스트 너비 계산 (Salary 컴포넌트와 동일)
  const calculateTextWidth = (text) => {
    if (!text) return 0;

    // text에 \n이 포함되어 있으므로 split 한 이후 가장 긴 텍스트를 기준으로 너비를 계산
    const textArray = text.split('\n');
    text = textArray.reduce((max, current) => {
      return Math.max(max, current.length);
    }, 0);
    
    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charCode = char.charCodeAt(0);
      
      // 한글 범위 체크 (가-힣, ㄱ-ㅎ, ㅏ-ㅣ)
      if ((charCode >= 0xAC00 && charCode <= 0xD7A3) || // 한글 완성형
          (charCode >= 0x3131 && charCode <= 0x318E) || // 한글 자모
          (charCode >= 0x1100 && charCode <= 0x11FF)) { // 한글 자모 확장
        width += 16; // 한글은 16px
      } else if (charCode >= 0x0020 && charCode <= 0x007F) {
        // ASCII 영문자, 숫자, 특수문자
        width += 8; // 영문자는 8px
      } else {
        // 기타 문자 (중국어, 일본어 등)
        width += 12; // 기본 12px
      }
    }
    
    return width;
  };


  // 첫 번째 데이터에서 헤더 추출 (조건부 반환 전에 실행)
  const firstRecord = dataArray.length > 0 ? dataArray[0] : null;
  const allHeaders = firstRecord ? Object.keys(firstRecord).filter(key => 
    !key.includes('기초_|+인적정보') && 
    !key.includes('from_year') && 
    !key.includes('from_month') && 
    !key.includes('to_year') && 
    !key.includes('to_month') && 
    !key.includes('name') && 
    !key.includes('id_number')
  ) : [];

  // 데이터가 있는 컬럼만 필터링 (빈 값 및 0도 미표기)
  const headers = allHeaders.filter(header => {
    return dataArray.some(row => {
      const value = row[header];
      if (value === null || value === undefined || value === '') return false;
      const s = String(value).trim();
      if (s === '') return false;
      const n = parseFloat(s);
      if (!isNaN(n) && n === 0) return false;
      return true;
    });
  });

  // 셀 클릭 핸들러
  const handleCellClick = (rowIndex, colIndex, value, header) => {
    const cellKey = `${rowIndex}-${colIndex}`;
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellKey)) {
        newSet.delete(cellKey);
      } else {
        newSet.add(cellKey);
      }
      return newSet;
    });
  };

  // 헤더 클릭 핸들러 (컬럼 전체 선택/해제)
  const handleHeaderClick = (header) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(header)) {
        newSet.delete(header);
        // 해당 컬럼의 모든 셀도 선택 해제
        setSelectedCells(prevCells => {
          const newCellSet = new Set(prevCells);
          dataArray.forEach((row, rowIndex) => {
            const colIndex = headers.indexOf(header);
            if (colIndex !== -1) {
              newCellSet.delete(`${rowIndex}-${colIndex}`);
            }
          });
          return newCellSet;
        });
      } else {
        newSet.add(header);
        // 해당 컬럼의 모든 셀을 선택
        setSelectedCells(prevCells => {
          const newCellSet = new Set(prevCells);
          dataArray.forEach((row, rowIndex) => {
            const colIndex = headers.indexOf(header);
            if (colIndex !== -1) {
              newCellSet.add(`${rowIndex}-${colIndex}`);
            }
          });
          return newCellSet;
        });
      }
      return newSet;
    });
  };

  // 선택된 셀들의 값은 그대로 표시하고, 마지막 컬럼에서만 차감
  const filteredData = useMemo(() => {
    if (selectedCells.size === 0) return dataArray;

    return dataArray.map((row, rowIndex) => {
      const filteredRow = { ...row };
      
      // 해당 row에서 선택된 셀들의 값 합계 계산
      let selectedValuesSum = 0;
      headers.forEach((header, colIndex) => {
        const cellKey = `${rowIndex}-${colIndex}`;
        if (selectedCells.has(cellKey)) {
          // 선택된 셀의 값을 합계에 추가 (값은 그대로 표시)
          const originalValue = row[header];
          if (typeof originalValue === 'number') {
            selectedValuesSum += originalValue;
          }
        }
      });
      
      // 마지막 컬럼(퇴직금)에서 선택된 값들을 차감
      if (selectedValuesSum > 0) {
        const lastColumnIndex = headers.length - 1;
        const lastColumnHeader = headers[lastColumnIndex];
        const originalValue = row[lastColumnHeader];
        if (typeof originalValue === 'number') {
          filteredRow[lastColumnHeader] = originalValue - selectedValuesSum;
        }
      }
      
      return filteredRow;
    });
  }, [dataArray, selectedCells, headers]);

  // 헤더는 항상 모든 컬럼 표시 (숨기지 않음)
  const filteredHeaders = headers;

  // 각 컬럼의 최적 너비 계산 (헤더와 데이터 모두 고려)
  const columnWidths = useMemo(() => {
    const widths = {};
    filteredHeaders.forEach((header, colIndex) => {
      // 헤더 너비 계산
      const headerWidth = calculateTextWidth(formatHeader(header).join(' ')) + 16;
      
      // 데이터 셀들의 최대 너비 계산
      let maxDataWidth = 0;
      dataArray.forEach((row, rowIndex) => {
        const cellValue = formatCellValue(row[header]);
        const cellWidth = calculateTextWidth(cellValue) + 16;
        maxDataWidth = Math.max(maxDataWidth, cellWidth);
      });
      
      // 헤더와 데이터 중 더 큰 값 사용, 최소 100px
      widths[header] = Math.max(headerWidth, maxDataWidth, 100);
    });
    return widths;
  }, [filteredHeaders, dataArray]);

  // 합계 계산 (조건부 반환 전에 실행) - 선택된 셀의 값은 합계에서 제외
  const totals = useMemo(() => {
    if (!dataArray || dataArray.length === 0 || filteredHeaders.length === 0) {
      return {};
    }
    
    const totalsObj = {};
    filteredHeaders.forEach((header, colIndex) => {
      const isLastColumn = colIndex === filteredHeaders.length - 1;
      
      if (isLastColumn) {
        // 마지막 컬럼(퇴직금)은 filteredData 사용 (이미 선택된 셀들이 차감된 상태)
        totalsObj[header] = filteredData.reduce((sum, record) => {
          const value = record[header];
          if (typeof value === 'number') {
            return sum + value;
          }
          return sum;
        }, 0);
      } else {
        // 다른 컬럼들은 원본 데이터에서 선택된 셀 제외
        totalsObj[header] = dataArray.reduce((sum, record, rowIndex) => {
          const cellKey = `${rowIndex}-${colIndex}`;
          const isSelected = selectedCells.has(cellKey);
          const originalValue = record[header];
          
          if (typeof originalValue === 'number') {
            // 선택된 셀의 값은 합계에서 제외
            if (isSelected) {
              return sum; // 선택된 셀의 값은 합계에 포함하지 않음
            }
            // 선택되지 않은 셀의 값만 합계에 포함
            return sum + originalValue;
          }
          return sum;
        }, 0);
      }
    });
    return totalsObj;
  }, [dataArray, filteredHeaders, selectedCells, filteredData]);

  // 퇴직금 계산 (수정된 데이터에서 마지막 컬럼의 총합)
  const retirementNetPrice = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return 0;
    
    // 수정된 데이터에서 마지막 컬럼의 총합 계산
    const lastColumnIndex = headers.length - 1;
    const lastColumnHeader = headers[lastColumnIndex];
    
    return filteredData.reduce((sum, record) => {
      const value = record[lastColumnHeader];
      if (typeof value === 'number') {
        return sum + value;
      }
      return sum;
    }, 0);
  }, [filteredData, headers]);

  // ── DC 부담금: 월별 산입분 + 연도별 산입개월수 계산 ──
  // 규칙: 월 산입분 = 근로기간 ÷ 분모일수 (입사/퇴사월은 가용일수, 그 외 월력일수).
  //       원본 임금 0원인 월은 산입 0. 산입개월수 0인 연도는 전년도 부담금 승계.

  const parseYearMonth = (label) => {
    const m = /^(\d{4})년\s*(\d{1,2})월$/.exec(String(label || '').trim());
    return m ? { y: parseInt(m[1], 10), m: parseInt(m[2], 10) } : null;
  };

  const parseDate = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || '').trim());
    return m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : null;
  };

  const monthsMetaMap = useMemo(() => {
    const map = {};
    (retirementMeta?.months_meta || []).forEach(mm => { map[mm['연월']] = mm; });
    return map;
  }, [retirementMeta]);

  // 재직기간: 입사일(없으면 계약일) ~ 퇴사일. 퇴사일이 없으면 사람이 고른 퇴사 예정일,
  // 그것도 없으면 계약종료일, 그것도 없으면 마지막 대장월 말(+ 경고)
  const employment = useMemo(() => {
    const em = retirementMeta?.employee_meta || {};
    const start = parseDate(em.hire_date) || parseDate(em.contract_date);
    const planned = em.leave_date ? null : parseDate(plannedLeave);
    let end = parseDate(em.leave_date) || planned || parseDate(em.contract_end_date);
    const noLeaveWarning = !end;
    if (!end && dataArray.length > 0) {
      // data[0]가 가장 최근 월 — 그 월의 말일까지 재직 간주
      const ym = parseYearMonth(dataArray[0]['연월']);
      if (ym) end = new Date(ym.y, ym.m, 0); // 해당 월 말일
    }
    return { start, end, noLeaveWarning, planned, meta: em };
  }, [retirementMeta, dataArray, plannedLeave]); // eslint-disable-line react-hooks/exhaustive-deps

  // 월별 자동 산입분 (연월 → 0~1) + 산입 사유(비고)
  const autoCalc = useMemo(() => {
    const shares = {};
    const reasons = {};
    const lastHeader = headers[headers.length - 1];
    dataArray.forEach(row => {
      const label = row['연월'];
      const ym = parseYearMonth(label);
      if (!ym) return;

      // 원본 임금(차감 전) 0원 이하 → 산입 제외
      const originalWage = typeof row[lastHeader] === 'number' ? row[lastHeader] : parseFloat(row[lastHeader]) || 0;
      if (originalWage <= 0) { shares[label] = 0; reasons[label] = '임금 0원'; return; }

      const calendarDays = new Date(ym.y, ym.m, 0).getDate();
      const monthStart = new Date(ym.y, ym.m - 1, 1);
      const monthEnd = new Date(ym.y, ym.m, 0);
      const notes = [];

      // 분모일수: 입사/퇴사월이면 재직기간∩해당월의 가용일수, 아니면 월력일수
      const meta = monthsMetaMap[label] || {};
      let denom = parseFloat(meta.calendar_days);
      if (!isFinite(denom) || denom <= 0) denom = calendarDays;
      if (employment.start || employment.end) {
        const s = employment.start && employment.start > monthStart ? employment.start : monthStart;
        const e = employment.end && employment.end < monthEnd ? employment.end : monthEnd;
        const availDays = Math.floor((e - s) / 86400000) + 1;
        if (availDays <= 0) { shares[label] = 0; reasons[label] = '재직기간 외'; return; }
        if (availDays < denom) {
          denom = availDays; // 입사/퇴사월 가용일수 적용
          const isHireMonth = employment.start && employment.start >= monthStart && employment.start <= monthEnd;
          const isLeaveMonth = employment.end && employment.end >= monthStart && employment.end <= monthEnd;
          const kind = [isHireMonth ? '입사월' : null, isLeaveMonth ? '퇴사월' : null].filter(Boolean).join('·') || '재직 일부';
          notes.push(`${kind} — 가용 ${availDays}일 기준`);
        }
      }

      // 근로기간: 없거나 무효면 만근으로 간주
      let workDays = parseFloat(meta.work_days);
      if (!isFinite(workDays) || workDays <= 0) workDays = denom;
      if (workDays < denom) notes.push(`근태 ${workDays}/${denom}일`);

      // 내부 계산은 원값 유지 (표시만 소수 둘째 자리) — 반올림 시 극소 산입월에서 부담금 왜곡
      shares[label] = Math.min(1, Math.max(0, workDays / denom));
      reasons[label] = notes.join(', ');
    });
    return { shares, reasons };
  }, [dataArray, headers, monthsMetaMap, employment]);
  const autoShares = autoCalc.shares;

  // 월별 비고: 수기 수정 여부 + 자동 계산 사유
  const shareNote = (label) => {
    const parts = [];
    if (monthOverrides[label] !== undefined) {
      parts.push(`수기 수정 (자동 ${(autoShares[label] ?? 0).toFixed(2)})`);
    }
    if (autoCalc.reasons[label]) parts.push(autoCalc.reasons[label]);
    return parts.join(', ');
  };

  const effectiveShare = (label) =>
    monthOverrides[label] !== undefined ? monthOverrides[label] : (autoShares[label] ?? 0);

  // 연도별 DC 부담금 = W × T ÷ (T − L) × 1/12  (계산은 retirement/dcContribution.js)
  //   W 임금총액(차감 반영) · T 근로관계 월수(휴직 포함) · T − L 실제 임금이 발생한 월수(월별 산입분의 합)
  //   12개월 근로면 W ÷ (12 − L), 입사·퇴사 연도는 W × T ÷ (T − L) ÷ 12, 그 해 전체 휴직이면 직전 연도 승계
  const yearlySummary = useMemo(() => {
    if (!filteredData.length || !headers.length) return [];
    const lastHeader = headers[headers.length - 1];
    const months = filteredData.map((row) => {
      const ym = parseYearMonth(row['연월']);
      if (!ym) return null;
      const wage = typeof row[lastHeader] === 'number' ? row[lastHeader] : parseFloat(row[lastHeader]) || 0;
      return { y: ym.y, wage, share: effectiveShare(row['연월']) };
    }).filter(Boolean);
    return yearlyContributions(months, employment.start, employment.end);
  }, [filteredData, headers, autoShares, monthOverrides, employment]); // eslint-disable-line react-hooks/exhaustive-deps

  // 렌더링용 행 목록: 최신 월부터 내림차순
  const displayRows = useMemo(() => {
    const rows = filteredData.map((row, i) => ({
      label: row['연월'], ym: parseYearMonth(row['연월']), row, dataIndex: i,
    }));
    rows.sort((a, b) => ((b.ym?.y || 0) - (a.ym?.y || 0)) || ((b.ym?.m || 0) - (a.ym?.m || 0)));
    return rows;
  }, [filteredData]);

  const totalContribution = yearlySummary.reduce((s, yr) => s + yr.contribution, 0);

  // 산입분 셀 클릭 → prompt로 확인 + 입력을 한 번에 처리
  const handleShareClick = (label) => {
    const input = window.prompt(
      `${label}의 산입 수치를 입력하세요 (0 ~ 1).\n빈 값으로 확인하면 자동 계산값(${(autoShares[label] ?? 0).toFixed(2)})으로 복원됩니다.`,
      effectiveShare(label).toFixed(2)
    );
    if (input === null) return; // 취소
    applyShareEdit(label, input);
  };

  const applyShareEdit = (label, rawValue) => {
    const trimmed = String(rawValue ?? '').trim();
    if (trimmed === '') {
      // 빈 값 입력 → 자동 계산값으로 복원
      setMonthOverrides(prev => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
      return;
    }
    const v = parseFloat(trimmed);
    if (!isFinite(v)) {
      window.alert('0과 1 사이의 숫자를 입력해주세요.');
      return;
    }
    const clamped = Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;
    setMonthOverrides(prev => ({ ...prev, [label]: clamped }));
  };

  // 선택된 셀들을 고려한 차감된 값 계산
  const calculateDeductedAmount = (rowIndex, originalAmount) => {
    let deductedAmount = originalAmount;
    
    console.log(`\n--- 행 ${rowIndex} 차감 계산 시작 ---`);
    console.log(`원본 금액: ${originalAmount}`);
    console.log(`선택된 셀들: ${Array.from(selectedCells).filter(key => key.startsWith(`${rowIndex}-`))}`);
    
    // 해당 행에서 선택된 셀들의 값을 차감
    filteredHeaders.forEach((header, colIndex) => {
      const cellKey = `${rowIndex}-${colIndex}`;
      if (selectedCells.has(cellKey)) {
        const cellValue = filteredData[rowIndex][header];
        if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
          const numericValue = parseFloat(String(cellValue).replace(/,/g, ''));
          if (!isNaN(numericValue)) {
            console.log(`선택된 셀 ${cellKey} (${header}): ${cellValue} -> ${numericValue} 차감`);
            console.log(`차감 전: ${deductedAmount}, 차감 후: ${deductedAmount - numericValue}`);
            deductedAmount -= numericValue;
          }
        }
      }
    });
    
    console.log(`최종 차감 금액: ${deductedAmount}`);
    return Math.max(0, deductedAmount); // 음수가 되지 않도록
  };

  // 담기 버튼 클릭 핸들러
  const handleAddToCartClick = () => {
    if (!onAddToCart) return;

    // 선택된 셀들을 고려한 차감된 데이터 + 연도별 부담금 요약 전달
    onAddToCart(filteredData, employee, company, yearlySummary, employment.noLeaveWarning);
  };

  // 데이터가 없을 때 처리
  if (!dataArray || dataArray.length === 0) {
    return (
      <div className="employee-retirement-table">
        <div className="no-data-message">
          <h3>퇴직정산 데이터가 없습니다</h3>
          <p>해당 직원의 급여 데이터를 찾을 수 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-retirement-table">

      {/* 메타 정보 영역 */}
      <div className="meta-section">
        <div className="meta-info">
          <span>직원명: {firstRecord['기초_|+인적정보_|+성명_|+nan'] || employee?.name}</span>
          <span>주민번호: {firstRecord['기초_|+인적정보_|+주민번호_|+nan'] || employee?.id_number}</span>
          <span>데이터 수: {dataArray.length}개</span>
          <span>표시 컬럼: {filteredHeaders.length}개</span>
          {allHeaders.length > headers.length && (
            <span className="hidden-columns-info">
              숨겨진 컬럼: {allHeaders.length - headers.length}개
            </span>
          )}
          {selectedCells.size > 0 && (
            <span className="selected-cells-info">
              선택된 셀: {selectedCells.size}개
            </span>
          )}
          {selectedColumns.size > 0 && (
            <span className="selected-columns-info">
              선택된 컬럼: {selectedColumns.size}개
            </span>
          )}
          <span className="retirement-net-price-info">
            임금총액(차감후): {formatCellValue(retirementNetPrice)}원
          </span>
          <span className="total-contribution-info">
            총 부담금: {totalContribution.toLocaleString()}원
          </span>
        </div>
        <div className="meta-info employment-meta">
          <span>입사일: {employment.meta.hire_date || '-'}</span>
          <span>퇴사일: {employment.meta.leave_date || '-'}</span>
          <span>계약일: {employment.meta.contract_date || '-'}</span>
          <span>계약종료일: {employment.meta.contract_end_date || '-'}</span>
          {employment.noLeaveWarning && employment.end && (
            <span className="no-leave-warning">
              ⚠ 퇴사일 미입력 — {employment.end.getFullYear()}-{String(employment.end.getMonth() + 1).padStart(2, '0')}까지 재직 간주
            </span>
          )}
        </div>
      </div>

      {/* 퇴사일이 없는 직원: 퇴사 예정일을 골라 그 날까지를 근로관계로 계산한다 */}
      {!employment.meta.leave_date && onPlannedLeaveChange && (
        <div className={`planned-leave-panel ${employment.planned ? 'set' : ''}`}>
          <div>
            <b>⚠ 퇴사일이 없습니다 — 퇴사 예정일을 고르세요</b>
            <span>
              {employment.planned
                ? `퇴사 예정일 ${plannedLeave}까지를 근로관계로 보고 부담금을 계산합니다(대장이 없는 달은 그때까지의 월평균으로 봅니다).`
                : `고르기 전에는 ${employment.meta.contract_end_date ? `계약종료일 ${employment.meta.contract_end_date}` : '마지막 대장 달 말일'}까지 재직으로 보고 계산합니다.`}
            </span>
          </div>
          <label>
            퇴사 예정일
            <input type="date" value={plannedLeave || ''} onChange={(e) => onPlannedLeaveChange(e.target.value || null)} />
          </label>
          {plannedLeave && <button type="button" onClick={() => onPlannedLeaveChange(null)}>지우기</button>}
        </div>
      )}

      {/* 휴직 내역(임금대장의 휴직 관리) */}
      {(retirementMeta?.leaves || []).length > 0 && (
        <div className="leave-history-panel">
          <b>휴직 내역</b>
          {retirementMeta.leaves.map((lv) => (
            <span key={lv.id || lv.start} className="leave-chip">
              {lv.reason || '휴직'} {lv.start} ~ {lv.end || '(종료일 없음)'}
            </span>
          ))}
          <span className="leave-history-note">휴직 기간은 근로관계 T 에 들어가고, 임금이 없는 만큼 L 로 빠집니다.</span>
        </div>
      )}

      {/* 연도별 부담금 정산 패널 */}
      <div className="yearly-summary-panel">
        <div className="yearly-formula">
          <span className="dc-math dc-main">
            <span className="dc-word">부담금</span> =
            <Frac top={<><V>W</V> × <V>T</V></>} bottom={<>(<V>T</V> − <V>L</V>) × 12</>} />
          </span>
          <span className="dc-legend">
            <span><V>W</V> 그 해 임금총액</span>
            <span><V>T</V> 근로관계 월수(휴직 포함)</span>
            <span><V>L</V> 휴직 등 임금이 없는 월수</span>
          </span>
          <span className="dc-cases">
            12개월 근로면 <span className="dc-math"><Frac top={<V>W</V>} bottom={<>12 − <V>L</V></>} /></span>
            · 그 해 임금이 0개월이면 직전 연도 부담금 승계(12개월 미만은 × <span className="dc-math"><Frac top={<V>T</V>} bottom="12" /></span>)
          </span>
        </div>
        <table className="yearly-summary-table">
          <thead>
            <tr>
              <th>연도</th>
              <th title="W: 그 해 임금총액(선택한 셀 차감 반영)">임금총액 W</th>
              <th title="T: 그 해 근로관계가 유지된 월수(휴직 기간 포함)">근로관계 T</th>
              <th title="L: 휴직 등 임금이 발생하지 않은 월수 = T − 산입개월수">휴직 등 L</th>
              <th title="T − L: 실제 임금이 발생한 월수(월별 산입분의 합)">산입개월수</th>
              <th>부담금</th>
              <th>산식(실제 값)</th>
            </tr>
          </thead>
          <tbody>
            {yearlySummary.map(yr => (
              <tr key={yr.year}>
                <td>{yr.year}년</td>
                <td className="num">{Math.round(yr.wageTotal).toLocaleString()}</td>
                <td className="num">{yr.employed}</td>
                <td className="num">{yr.leave.toFixed(2)}</td>
                <td className="num">{yr.worked.toFixed(2)}</td>
                <td className="num">{yr.contribution.toLocaleString()}</td>
                <td className="note">
                  {yr.calc && yr.calc.kind === 'formula' ? (
                    <span className="dc-math dc-cell" title={yr.rule}>
                      {yr.calc.note && <span className="dc-note">{yr.calc.note}</span>}
                      <Frac top={yr.calc.top} bottom={yr.calc.bottom} /> = <b>{yr.calc.result}</b>
                    </span>
                  ) : (
                    <span className={yr.inheritedFrom !== null ? 'inherited-tag' : ''} title={yr.rule}>
                      {yr.calc ? `${yr.calc.note} ${yr.calc.result}` : yr.rule}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>총 부담금</td>
              <td className="num total">{totalContribution.toLocaleString()}</td>
              <td className="note"></td>
            </tr>
          </tfoot>
        </table>
      </div>

        {/* 담기 버튼 */}
        <div className="cart-actions">
          <button 
            className="add-to-cart-btn"
            onClick={handleAddToCartClick}
            disabled={!onAddToCart}
            title="퇴직정산 담기"
          >
            🛒 퇴직정산 담기
          </button>
        </div>

      {/* 테이블 영역 */}
      <div className="retirement-table-display">
        <div className="retirement-table-container">
          <table className="retirement-data-table">
            <thead>
              <tr>
                 <th className="ledger-link-header">
                   <div className="header-part">대장</div>
                 </th>
                 {filteredHeaders.map((header, index) => {
                   const isColumnSelected = selectedColumns.has(header);
                   const isFirstColumn = index === 0;
                   const isLastColumn = index === filteredHeaders.length - 1;
                   const isClickable = !isFirstColumn && !isLastColumn;
                   
                   return (
                     <th
                       key={index}
                       className={`${isClickable ? 'clickable-header' : 'non-clickable-header'} ${isColumnSelected ? 'selected-column' : ''} ${isLastColumn ? 'retirement-column' : ''}`}
                       style={{ 
                         minWidth: `${columnWidths[header]}px`,
                         width: `${columnWidths[header]}px`
                       }}
                       onClick={isClickable ? (e) => {
                         e.stopPropagation();
                         handleHeaderClick(header);
                       } : undefined}
                       title={isClickable ? `클릭하여 컬럼 전체 선택/해제` : isFirstColumn ? '첫 번째 컬럼 (선택 불가)' : '마지막 컬럼 (선택 불가)'}
                     >
                       {formatHeader(header).map((part, partIndex) => (
                         <div key={partIndex} className="header-part">{part}</div>
                       ))}
                       {isColumnSelected && <span className="column-selected-indicator">✓</span>}
                       {isLastColumn && <span className="retirement-indicator">💰</span>}
                     </th>
                   );
                 })}
                 <th className="share-column-header" title="월별 산입분 (근로기간 ÷ 분모일수). 클릭하여 수기 수정 가능">
                   <div className="header-part">산입</div>
                 </th>
                 <th className="share-note-header">
                   <div className="header-part">비고</div>
                 </th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((entry, displayIndex) => {
                const { label, ym } = entry;
                const prevYm = displayIndex > 0 ? displayRows[displayIndex - 1].ym : null;
                const isYearBoundary = prevYm && ym && prevYm.y !== ym.y;
                const ledgerLinkCell = (
                  <td className="ledger-link-cell">
                    {ym && company?.id ? (
                      <a
                        className="ledger-link-btn"
                        href={`/payroll?company=${encodeURIComponent(company.id)}&year=${ym.y}&month=${ym.m}`}
                        target="payroll-ledger"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          // 새 창(팝업 창)으로 연다. 지금 탭은 그대로 두고, 같은 창을 다시 쓴다
                          const win = window.open(e.currentTarget.href, 'payroll-ledger', 'width=1500,height=900,resizable=yes,scrollbars=yes');
                          if (win) { e.preventDefault(); win.focus(); }
                        }}
                        title={`${label} 임금대장 새 창에서 보기`}
                      >
                        보기
                      </a>
                    ) : null}
                  </td>
                );
                const shareCell = (() => {
                  const isOverridden = monthOverrides[label] !== undefined;
                  return (
                    <td
                      className={`share-cell ${isOverridden ? 'overridden' : ''}`}
                      onClick={() => handleShareClick(label)}
                      title={isOverridden
                        ? `수기 수정됨 (자동: ${(autoShares[label] ?? 0).toFixed(2)}) — 클릭 후 빈 값 입력 시 자동값 복원`
                        : '클릭하여 산입 수치 수정'}
                    >
                      {effectiveShare(label).toFixed(2)}
                      {isOverridden && <span className="override-mark">✎</span>}
                    </td>
                  );
                })();
                const noteCell = (
                  <td className="share-note-cell" title={shareNote(label)}>
                    {shareNote(label)}
                  </td>
                );

                const row = entry.row;
                const rowIndex = entry.dataIndex;
                return (
                <tr key={label} className={isYearBoundary ? 'year-boundary' : ''}>
                   {ledgerLinkCell}
                   {filteredHeaders.map((header, colIndex) => {
                     const cellKey = `${rowIndex}-${colIndex}`;
                     const isSelected = selectedCells.has(cellKey);
                     const isFirstColumn = colIndex === 0;
                     const isLastColumn = colIndex === filteredHeaders.length - 1;
                     const isClickable = !isFirstColumn && !isLastColumn;

                     return (
                       <td
                         key={colIndex}
                         className={`${isSelected ? 'selected-cell' : isClickable ? 'clickable-cell' : 'non-clickable-cell'} ${isLastColumn ? 'retirement-cell' : ''}`}
                         style={{ 
                           minWidth: `${columnWidths[header]}px`,
                           width: `${columnWidths[header]}px`
                         }}
                         onClick={isClickable ? () => handleCellClick(rowIndex, colIndex, row[header], header) : undefined}
                         title={isClickable ? (isSelected ? '선택된 셀 (클릭하여 해제)' : '클릭하여 선택') : isFirstColumn ? '첫 번째 컬럼 (선택 불가)' : '마지막 컬럼 (선택 불가)'}
                       >
                         {formatCellValue(row[header])}
                       </td>
                     );
                   })}
                   {shareCell}
                   {noteCell}
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td className="ledger-link-cell"></td>
                <td className="totals-label">합계</td>
                {filteredHeaders.slice(1).map((header, index) => (
                  <td 
                    key={index} 
                    className={`totals-cell ${index === filteredHeaders.slice(1).length - 1 ? 'retirement-cell' : ''}`}
                    style={{ 
                      minWidth: `${columnWidths[header]}px`,
                      width: `${columnWidths[header]}px`
                    }}
                  >
                    {formatCellValue(totals[header])}
                  </td>
                ))}
                <td className="share-cell totals-cell">
                  {yearlySummary.reduce((s, yr) => s + yr.months, 0).toFixed(2)}
                </td>
                <td className="share-note-cell"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="footer-info">
          <p>※ 퇴직정산 계산을 위한 급여 데이터입니다.</p>
          <p>※ 데이터가 없는 컬럼은 자동으로 숨겨집니다.</p>
          <p>※ 셀을 클릭하면 선택되며, 선택된 셀의 값은 그대로 표시됩니다.</p>
          <p>※ 헤더를 클릭하면 해당 컬럼 전체를 선택/해제할 수 있습니다.</p>
          <p>※ 각 행의 마지막 컬럼(퇴직금)은 해당 행에서 선택된 셀들의 값을 차감한 금액입니다.</p>
          <p>※ 산입 컬럼은 월별 산입분(근로기간 ÷ 분모일수, 입사·퇴사월은 가용일수 기준)입니다. 임금 0원인 월은 산입 제외됩니다.</p>
          <p>※ 병가 등 예외는 산입 컬럼을 클릭해 수기로 수정하세요. 산입개월수가 0인 연도의 부담금은 직전 연도 값을 승계합니다.</p>
          <p>※ 연도별 부담금 = W × T ÷ (T − L) × 1/12. 12개월 근로면 W ÷ (12 − L), 입사·퇴사 연도(복직 없이 퇴사 포함)는 근로관계 월수 T 로 보정합니다(고용노동부 해석).</p>
        </div>
        
      </div>
    </div>
  );
}

export default EmployeeRetirementTable;
